import styled from 'styled-components';

import { pxToRem } from 'utils/convert';
import { Checkmark } from 'ui/icons/action/Checkmark';
import { Subtract16 } from 'ui/icons/aurora/16px/Subtract16';
import { colors, spacing, subtleShadow, easing, duration } from 'ui/theme';

import { CHECKBOX_STATUS } from './types';

const IconWrapper = styled.span`
  align-items: center;
  display: inline-flex;
  justify-content: center;
  min-height: ${spacing.half};
  svg:not(.shape) path {
    stroke: ${colors.iconOnColor};
  }
  svg.shape {
    color: ${colors.iconOnColor};
  }
  svg[data-temporary-fix] path {
    fill: ${colors.iconOnColor};
    stroke: none;
  }
`;

const CheckboxBackground = styled.div`
  align-items: center;
  background: ${({ isDisabled, isUnchecked }) => {
    if (isDisabled) {
      return isUnchecked ? colors.grey5 : colors.grey35;
    }
    if (isUnchecked) return colors.white;
    return colors.green100;
  }};
  border: solid ${spacing.single}
    ${({ isUnchecked, isDisabled }) => {
      if (isDisabled) return colors.grey35;
      if (isUnchecked) return colors.grey25;
      return colors.green100;
    }};
  border-radius: ${spacing.quarter};
  display: inline-flex;
  height: ${spacing.base};
  line-height: ${spacing.base};
  justify-content: center;
  overflow: hidden;
  text-align: center;
  width: ${spacing.base};
  min-width: ${spacing.base};
  box-shadow: ${subtleShadow.boxShadow};
  transition: all ${duration.quick} ${easing.entrance};
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};

  &:hover {
    border: solid ${spacing.single}
      ${({ isUnchecked, isDisabled }) => {
        if (isDisabled || isUnchecked) return colors.grey35;
        return colors.green100;
      }};
  }

  &:focus {
    outline: solid ${spacing.single}
      ${({ isUnchecked, isDisabled }) => {
        if (isDisabled || isUnchecked) return 'none';
        return colors.white;
      }};
    box-shadow: 0 0 0 ${pxToRem(2.5)}
      ${({ isUnchecked, isDisabled }) => {
        if (isDisabled) return 'none';
        if (isUnchecked) return colors.grey15;
        return colors.green50;
      }};
  }
`;

/**
  A static checkbox is a non-interactive version of a checkbox for use as a visual in other components (e.g. a selectable row with its own ARIA attributes)
 */
export const StaticCheckbox = ({ isDisabled, status, ...props }) => {
  // toggling the styling is more declarative if we have easy flags
  // to toggle styles against
  const isChecked = status === CHECKBOX_STATUS.CHECKED;
  const isUnchecked = status === CHECKBOX_STATUS.UNCHECKED;
  const isIndeterminate = !isChecked && !isUnchecked;

  return (
    <CheckboxBackground
      aria-checked={isIndeterminate ? 'mixed' : isChecked}
      aria-disabled={isDisabled}
      data-test="static-checkbox"
      isChecked={isChecked}
      isDisabled={isDisabled}
      isUnchecked={isUnchecked}
      role="checkbox"
      status={status}
      {...props}
    >
      <IconWrapper data-test="checkbox-icon-wrapper" isDisabled={isDisabled}>
        {isIndeterminate && <Subtract16 data-test="subtract-icon" width={14} />}
        {isChecked && <Checkmark color={colors.white} data-test="check-icon" height={8} />}
      </IconWrapper>
    </CheckboxBackground>
  );
};
